import {
  BoltCustomFieldConfiguration,
  BoltTaskTypePriorities,
  IBoltTaskSLA,
  IBoltTaskSOP,
  IBoltTaskTemplate,
  INewBoltTask,
  SLAUnits,
} from "@bolt/core";
import {
  FieldSchemaFormBuilderV2,
  RichTextEditorFieldProps,
  RichTextEditorValueType,
  SchemaFactoryV2,
  SingleCheckboxFieldProps,
  fieldTypes,
  ComplexFieldProps,
  utils,
  EnumSingleSelectFieldV2Props,
  AsyncODataDropdownFieldProps,
  AsyncAutoCompleteFieldProps,
} from "@ucl/library";

import {
  TaskTypeAsyncAutoCompleteProps,
  taskTypeAsyncODataDropdownConfig,
} from "../../config/taskTypeAsyncAutoComplete";
import { SLAComponent } from "../../SlaComponent/SlaComponent";
import { SOPComponent } from "../../SOPComponent/SOPComponent";

import {
  assigneeAsyncAutoCompleteConfig,
  AssigneeAsyncAutoCompleteProps,
} from "../../config/assigneeAsyncAutoComplete";
import { RichTextEditorsOutputTextTypes } from "../../../types/globalTypes";

import {
  TaskExecutionTypeComponent,
  TaskExecutionTypeComponentValue,
} from "../../TaskExecutionTypeComponent/TaskExecutionTypeComponent";
import { taskPriorityOptions } from "../../TaskTemplateForm/builders/CreateBoltTaskTemplateFormContainer";

///
/// 1. I need an ability for client app to inject cusom configuration into field schema
///

export const createBoltTaskBuilder: (
  factory: SchemaFactoryV2,
  options: {
    taskTypeProps: TaskTypeAsyncAutoCompleteProps;
    assigneeProps: AssigneeAsyncAutoCompleteProps;
    fieldConfigurationProps: BoltCustomFieldConfiguration;
    richTextEditorsOutputTextType?: RichTextEditorsOutputTextTypes;
    taskExecutionTypeData?: {
      getAuthRequestHeader: () => Promise<Record<string, string> | undefined>;
      apiBaseUrl: string;
    };
  }
) => FieldSchemaFormBuilderV2<INewBoltTask> =
  (factory, options) => (onValueChange, form, errors) => {
    const richTextEditorOutputTypeIsPlainText =
      !utils.miscellaneous.isNullOrUndefined(
        options?.richTextEditorsOutputTextType
      ) &&
      options.richTextEditorsOutputTextType ===
        RichTextEditorsOutputTextTypes.PLAIN;

    const formFields = [
      ...factory.buildFieldSchemas<INewBoltTask>(
        "BoltTask",
        [
          // {
          //   fieldKey: "BoltTask__FieldConfigurations",
          //   ...options.fieldConfigurationProps.props,
          // },
          // TODO Validate, but by domain this will always be New, so dont show on create
          // {
          //   fieldKey: "BoltTask__BoltTaskStatus",
          //   value: BoltTaskStatuses.New,
          //   optionValues: get(
          //     factory.schema,
          //     "enumOptions.BoltTaskStatuses"
          //   ) as unknown as fieldTypes.Option<number>[],
          //   readOnly: true,
          //   disabled: true,
          // } as EnumSingleSelectFieldV2Props,
          {
            ...taskTypeAsyncODataDropdownConfig(options.taskTypeProps),
            fieldKey: "BoltTask__TaskTemplateId",
            isRequired: true,
            errors: errors,
            type: "AsyncODataDropdown",
            onSubmit: function (value: fieldTypes.AsyncODataDropdownFieldValue) {
              const boltTaskTemplate = value as unknown as IBoltTaskTemplate;
              onValueChange(
                {
                  ...form,
                  entityType: boltTaskTemplate?.entityType,
                  assigneeId: form.assigneeId ?? boltTaskTemplate?.assigneeId,
                  assigneeName:
                    form.assigneeId ?? boltTaskTemplate?.assigneeName,
                  stepName: boltTaskTemplate?.stepName as string,
                  taskTemplateId: boltTaskTemplate?.id || "",
                  taskTemplateName: boltTaskTemplate?.taskTemplateName,
                  taskTemplateDescription:
                    boltTaskTemplate?.taskTemplateDescription,
                  taskTemplateSOP: boltTaskTemplate?.taskTemplateSOP,
                  taskTemplateSOP_AsList:
                    boltTaskTemplate?.taskTemplateSOP_AsList,
                  sla: boltTaskTemplate?.sla ?? 0,
                  slaUnit: boltTaskTemplate?.slaUnit ?? SLAUnits.Day,
                  workflow: !!boltTaskTemplate?.hasMultiSteps
                    ? "Multi-Step"
                    : "Single Step",
                  //taskExecutionType: boltTaskTemplate.taskExecutionType ?? "",
                  // taskExecutionTypeFields:
                  //   boltTaskTemplate.taskExecutionTypeFields,
                  taskPriority:
                    boltTaskTemplate?.taskPriority ??
                    BoltTaskTypePriorities.Normal,
                },
                boltTaskTemplate?.id || "",
                this as fieldTypes.BaseFieldProps<fieldTypes.CoreFieldValue>
              );
            },
          } as AsyncODataDropdownFieldProps,
          {
            fieldKey: "BoltTask__TaskExecutionTypeFields",
            disabled: true,
            complexFieldValue: {
              key: form?.taskTemplateId,
              taskExecutionType: form?.taskExecutionType,
              entityType: form?.entityType,
              factory,
              fieldSet: "TaskExecutionTypeContext",
              getAuthRequestHeader:
                options.taskExecutionTypeData?.getAuthRequestHeader,
              apiBaseUrl: options.taskExecutionTypeData?.apiBaseUrl,
              errors,
              isReadOnly: true,
            } as TaskExecutionTypeComponentValue,
          },
          {
            fieldKey: "BoltTask__TaskTemplateDescription",
            value: form.taskTemplateDescription,
            valueType: RichTextEditorValueType.html,
            readOnly: true,
            richTextEditorSettings: {
              toolbarSettings: {
                enable: richTextEditorOutputTypeIsPlainText ? false : true,
              },
            },
            getPlainTextOnSubmit: function (des) {
              if (richTextEditorOutputTypeIsPlainText) {
                onValueChange(
                  { ...form, taskTemplateDescription: des || "" },
                  des,
                  this
                );
              }
            },
            onSubmit: function (des) {
              if (!richTextEditorOutputTypeIsPlainText) {
                onValueChange(
                  { ...form, taskTemplateDescription: des || "" },
                  des,
                  this
                );
              }
            },
          } as RichTextEditorFieldProps,
          {
            fieldKey: "BoltTask__StepName",
            hidden: form.workflow != "Multi-Step",
          },
          {
            ...assigneeAsyncAutoCompleteConfig(options.assigneeProps),
            fieldKey: "BoltTask__AssigneeId",
            fieldName: "BoltTask__AssigneeId",
            value: form.assigneeId,
            isRequired: true,
            errors: errors,
            onSubmit: function (value) {
              const optionValue = value as unknown as fieldTypes.Option<string>;
              onValueChange(
                { ...form, assigneeId: optionValue.value },
                optionValue.value,
                this as fieldTypes.BaseFieldProps<fieldTypes.CoreFieldValue>
              );
            },
          } as AsyncAutoCompleteFieldProps,
          {
            fieldKey: "BoltTask__IsRush",
            width: "50",
            onSubmit: function (value) {
              onValueChange({ ...form, isRush: value as boolean }, value, this);
            },
          } as SingleCheckboxFieldProps,
          {
            fieldKey: "BoltTask__TaskTemplateSOP_AsObject",
            label: "",
            type: "Complex",
            value: {
              sop: form.taskTemplateSOP,
              sop_AsList: form.taskTemplateSOP_AsList,
            },
            readOnly: true,
            renderer: SOPComponent,
          } as ComplexFieldProps<IBoltTaskSOP>,
          {
            type: "Complex",
            fieldKey: "BoltTask__SLA_AsObject",
            value: {
              sla: form.sla,
              slaUnit: form.slaUnit,
            },
            readOnly: true,
            availableSLAUnits: [SLAUnits.Day, SLAUnits.Hour],
          } as unknown as ComplexFieldProps<IBoltTaskSLA>,
          {
            fieldKey: "BoltTask__TaskPriority",
            type: "EnumSingleSelect",
            value: form.taskPriority,
            optionValues: taskPriorityOptions,
            isRequired: true,
            errors: errors,
            onSubmit: (value) => {
              onValueChange(
                { ...form, taskPriority: value as BoltTaskTypePriorities },
                value,
                this as unknown as fieldTypes.BaseFieldProps<fieldTypes.CoreFieldValue>
              );
            },
          } as EnumSingleSelectFieldV2Props,
          {
            fieldKey: "BoltTask__Detail",
            value: form.detail,
            valueType: RichTextEditorValueType.html,
            richTextEditorSettings: {
              toolbarSettings: {
                enable: richTextEditorOutputTypeIsPlainText ? false : true,
              },
            },
            isRequired: true,
            errors: errors,
            getPlainTextOnSubmit: function (detail) {
              if (richTextEditorOutputTypeIsPlainText) {
                onValueChange({ ...form, detail: detail || "" }, detail, this);
              }
            },
            onSubmit: function (detail) {
              if (!richTextEditorOutputTypeIsPlainText) {
                onValueChange({ ...form, detail: detail || "" }, detail, this);
              }
            },
          } as RichTextEditorFieldProps,
        ],
        onValueChange,
        form,
        errors,
        ["BoltTask__TaskTemplateId"],
        {
          BoltTaskSOP: SOPComponent,
          BoltTaskSLA: SLAComponent,
          BoltFieldConfigurations: options.fieldConfigurationProps.render,
          TaskExecutionTypeFieldsCollection: TaskExecutionTypeComponent,
        }
      ),
    ];

    return formFields;
  };
